import { useState } from "react";
import { WorkflowData } from "../WorkflowEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, GripVertical, ChevronDown, ChevronRight } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface PhasesTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

function SortablePhaseCard({ phase, index, onUpdate, onDelete, isOpen, onToggle }: any) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: phase.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  function addTask() {
    const currentTasks = Array.isArray(phase.tasks) ? phase.tasks : [];
    const newTasks = [...currentTasks, ""];
    onUpdate({ ...phase, tasks: newTasks });
  }

  function updateTask(taskIndex: number, value: string) {
    const currentTasks = Array.isArray(phase.tasks) ? [...phase.tasks] : [];
    currentTasks[taskIndex] = value;
    onUpdate({ ...phase, tasks: currentTasks });
  }

  function removeTask(taskIndex: number) {
    const currentTasks = Array.isArray(phase.tasks) ? phase.tasks : [];
    const newTasks = currentTasks.filter((_: any, i: number) => i !== taskIndex);
    onUpdate({ ...phase, tasks: newTasks });
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="p-4">
        <Collapsible open={isOpen} onOpenChange={onToggle}>
          <div className="flex items-start gap-3">
            <button {...attributes} {...listeners} className="cursor-grab hover:cursor-grabbing mt-2">
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </button>
            
            <div className="flex-1 space-y-4">
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <h3 className="font-semibold">
                      Phase {index + 1}: {phase.name || "Untitled Phase"}
                    </h3>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phase Name</Label>
                    <Input
                      value={phase.name || ""}
                      onChange={(e) => onUpdate({ ...phase, name: e.target.value })}
                      placeholder="e.g., Prepare Documentation"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Time</Label>
                    <Input
                      value={phase.estimated_time || ""}
                      onChange={(e) => onUpdate({ ...phase, estimated_time: e.target.value })}
                      placeholder="e.g., 10 minutes"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={phase.description || ""}
                    onChange={(e) => onUpdate({ ...phase, description: e.target.value })}
                    placeholder="Describe what happens in this phase..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Tasks / Checklist</Label>
                    <Button variant="outline" size="sm" onClick={addTask}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Task
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(phase.tasks || []).map((task: string, taskIndex: number) => (
                      <div key={taskIndex} className="flex gap-2">
                        <Input
                          value={task}
                          onChange={(e) => updateTask(taskIndex, e.target.value)}
                          placeholder="Enter task description"
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeTask(taskIndex)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </div>
        </Collapsible>
      </Card>
    </div>
  );
}

export function PhasesTab({ data, onChange }: PhasesTabProps) {
  const [openPhases, setOpenPhases] = useState<Record<string, boolean>>({});
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function addPhase() {
    const currentPhases = Array.isArray(data.phases) ? data.phases : [];
    const newPhase = {
      id: `phase-${Date.now()}`,
      phase_number: currentPhases.length + 1,
      name: "",
      description: "",
      estimated_time: "",
      tasks: [],
      required_documents: [],
      training_videos: [],
    };
    onChange({ ...data, phases: [...currentPhases, newPhase] });
    setOpenPhases({ ...openPhases, [newPhase.id]: true });
  }

  function updatePhase(index: number, updatedPhase: any) {
    const currentPhases = Array.isArray(data.phases) ? [...data.phases] : [];
    currentPhases[index] = updatedPhase;
    onChange({ ...data, phases: currentPhases });
  }

  function deletePhase(index: number) {
    const currentPhases = Array.isArray(data.phases) ? data.phases : [];
    const newPhases = currentPhases.filter((_, i) => i !== index);
    const renumbered = newPhases.map((p, i) => ({ ...p, phase_number: i + 1 }));
    onChange({ ...data, phases: renumbered });
  }

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const currentPhases = Array.isArray(data.phases) ? data.phases : [];
      const oldIndex = currentPhases.findIndex(p => p.id === active.id);
      const newIndex = currentPhases.findIndex(p => p.id === over.id);
      const newPhases = arrayMove(currentPhases, oldIndex, newIndex);
      const renumbered = newPhases.map((p, i) => ({ ...p, phase_number: i + 1 }));
      onChange({ ...data, phases: renumbered });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Workflow Phases</h2>
          <p className="text-sm text-muted-foreground">
            Define the phases users will complete. Drag to reorder.
          </p>
        </div>
        <Button onClick={addPhase}>
          <Plus className="h-4 w-4 mr-2" />
          Add Phase
        </Button>
      </div>

      {data.phases.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">No phases yet</p>
          <Button onClick={addPhase} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Phase
          </Button>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={data.phases.map(p => p.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {data.phases.map((phase, index) => (
                <SortablePhaseCard
                  key={phase.id}
                  phase={phase}
                  index={index}
                  onUpdate={(updated: any) => updatePhase(index, updated)}
                  onDelete={() => deletePhase(index)}
                  isOpen={openPhases[phase.id]}
                  onToggle={() => setOpenPhases({ ...openPhases, [phase.id]: !openPhases[phase.id] })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}